import React, { useEffect, useCallback } from "react";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import { Alert } from "../../atoms/Alert";
import { InitialInterview } from "../../templates/InitialInterview";
import {
  updateApplicantRequest,
  resetIsUpdateSuccessful,
  fetchApplicantRequest,
  fetchApplicantsListRequest,
  fetchTotalApplicantsRequest,
  updateTotalNewApplicants,
  fetchApplicantsRequest,
  resetIsOverlap,
  checkScheduleRequest,
} from "../../../redux/applicant/actions";
import { FirebaseApplicant } from "../../../core/entities/applicant";
import { setSideNav } from "../../../redux/recruiter/actions";
import { clearSpecificRequest } from "../../../redux/request/action";
import {
  FETCH_APPLICANTS_REQUEST,
  UPDATE_APPLICANT_REQUEST,
  SEARCH_APPLICANT_REQUEST,
  FETCH_APPLICANT_REQUEST,
  FETCH_APPLICANTS_LIST_REQUEST,
} from "../../../redux/applicant/actionTypes";
import { getLogs, updateLog } from "../../../redux/logs/actions";
import { Log } from "../../../core/entities/log";
import { message } from "antd";
import { addNotificationRequest } from "./../../../redux/notification/actions";
import { NotificationType } from "../../../core/entities/notification";
import {
  clearInterviewers,
  fetchInterviewersRequest,
} from "../../../redux/interviewer/actions";

const InitialInterviewPage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const location: any = useLocation();

  const updateRequest = useSelector(
    (state: RootStateOrAny) => state.requestStore?.[UPDATE_APPLICANT_REQUEST]
  );

  const user = useSelector((state: RootStateOrAny) => state.auth.user);

  const logs = useSelector((state: RootStateOrAny) => state.logs.logs);

  const applicant = useSelector(
    (state: RootStateOrAny) => state.applicant.applicant
  );

  const schedule = useSelector((state: RootStateOrAny) => state.applicant);

  const applicantsList = useSelector(
    (state: RootStateOrAny) =>
      state.requestStore?.[FETCH_APPLICANTS_LIST_REQUEST]
  );

  const totalApplicants = useSelector(
    (state: RootStateOrAny) => state.applicant.totalApplicants
  );

  const interviewers = useSelector(
    (state: RootStateOrAny) => state.interviewer.interviewers
  );

  const dispatchUpdateApplicant = useCallback(
    (id: string, params: Partial<FirebaseApplicant>) =>
      dispatch(updateApplicantRequest(id, params)),
    [dispatch]
  );

  const dispatchUpdateLogs = useCallback(
    (applicantId: string, data: Log) => dispatch(updateLog(applicantId, data)),
    [dispatch]
  );
  const dispatchGetLogs = useCallback(
    (applicantId: string) => dispatch(getLogs(applicantId)),
    [dispatch]
  );
  const dispatchFetchApplicant = useCallback(
    (applicantId: string) => dispatch(fetchApplicantRequest(applicantId)),
    [dispatch]
  );

  const dispatchFetchInitialInterview = useCallback(
    (
      lastElementDocs?: unknown,
      limit = 10,
      subsStatus = undefined,
      position = undefined
    ) =>
      dispatch(
        fetchApplicantsListRequest({
          where: [
            ["applicationStatus.currentStatus", "==", "Initial Interview"],
            ["applicationStatus.subStatus", "in", subsStatus],
            ["positionApplied", "in", position],
            ["isDeleted", "==", false],
          ],
          orderBy: [["applicationStatus.dateMoved", "desc"]],
          limit,
          startAfter: lastElementDocs,
        })
      ),
    [dispatch]
  );

  const dispatchUpdateTotalNewApplicants = useCallback(
    (currentPhase: string, prevPhase: string, currentStatus: string) =>
      dispatch(
        updateTotalNewApplicants(currentPhase, prevPhase, currentStatus)
      ),
    [dispatch]
  );

  const dispatchResetIsOverlap = useCallback(
    () => dispatch(resetIsOverlap()),
    [dispatch]
  );

  const dispatchCheckSchedule = useCallback(
    (id: string, dateAndTime: string, params: Partial<FirebaseApplicant>) =>
      dispatch(checkScheduleRequest(id, dateAndTime, params)),
    [dispatch]
  );

  const dispatchFetchTotalApplicants = useCallback(
    () => dispatch(fetchTotalApplicantsRequest()),
    [dispatch]
  );

  const dispatchAddNotification = useCallback(
    (notification: NotificationType) =>
      dispatch(addNotificationRequest(notification)),
    [dispatch]
  );

  const isUpdating = useSelector(
    (state: RootStateOrAny) => state.applicant.isUpdating
  );

  useEffect(() => {
    dispatch(dispatchFetchInitialInterview());
    dispatch(dispatchFetchTotalApplicants());
    dispatch(clearSpecificRequest(SEARCH_APPLICANT_REQUEST));
    dispatch(fetchInterviewersRequest());

    return () => {
      dispatch(clearSpecificRequest(FETCH_APPLICANTS_REQUEST));
      dispatch(clearSpecificRequest(FETCH_APPLICANT_REQUEST));
      dispatch(clearSpecificRequest(UPDATE_APPLICANT_REQUEST));
      dispatch(clearSpecificRequest(FETCH_APPLICANTS_LIST_REQUEST));
      dispatch(resetIsUpdateSuccessful());
      dispatch(clearInterviewers());
    };
  }, [dispatch]);

  useEffect(() => {
    location.pathname.includes("/recruit/initial-interview") &&
      dispatch(setSideNav(true));
  }, [dispatch, location.pathname]);

  useEffect(() => {
    if (location.state?.applicantId) {
      if (location.state?.fromSearch) {
        return history.replace(
          `/recruit/technical-exam/${location.state?.applicantId}/true`
        );
      }
      history.push(`/recruit/initial-interview/${location.state?.applicantId}`);
    }
  }, [location, history]);

  useEffect(() => {
    if (
      updateRequest?.didSucceed &&
      !updateRequest?.isLoading &&
      updateRequest?.response?.interviewers &&
      Object.getOwnPropertyNames(updateRequest?.response).length > 1
    ) {
      dispatch(clearSpecificRequest(UPDATE_APPLICANT_REQUEST));
      message.config({ duration: 7, maxCount: 1 });
      message.success("Successfully sent an invite/event.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateRequest?.didSucceed]);

  const onClickApplicant = (key: any) => {
    return history.push(`/recruit/initial-interview/${key}`);
  };

  const total = totalApplicants?.initialInterview?.current || 0;
  const currentStatus =
    updateRequest?.response?.["applicationStatus.currentStatus"];
  const subStatus = updateRequest?.response?.["applicationStatus.subStatus"];

  return (
    <>
      <InitialInterview
        onUpdateApplicant={dispatchUpdateApplicant}
        isLoading={applicantsList?.isLoading}
        isOverlap={schedule?.isOverlap}
        isChecking={schedule?.isChecking}
        resetIsOverlap={dispatchResetIsOverlap}
        checkSchedule={dispatchCheckSchedule}
        tableData={applicantsList}
        onClickApplicant={onClickApplicant}
        user={user}
        isUpdating={isUpdating}
        error={updateRequest?.error}
        logs={logs}
        getLogs={dispatchGetLogs}
        updateLogs={dispatchUpdateLogs}
        applicant={applicant}
        fetchApplicant={dispatchFetchApplicant}
        updateApplicantRequest={updateRequest}
        total={total}
        updateTotalNewApplicants={dispatchUpdateTotalNewApplicants}
        fetchApplicants={dispatchFetchInitialInterview}
        fetchTotalApplicants={dispatchFetchTotalApplicants}
        onAddNotification={dispatchAddNotification}
        interviewers={interviewers}
      />
      {updateRequest?.didSucceed &&
        (currentStatus === "Failed" || subStatus === "Declined") && (
          <Alert
            type="success"
            message={
              currentStatus === "Failed"
                ? "Applicant moved to failed status"
                : "Successfully moved applicant’s status"
            }
            showIcon
          />
        )}
      {updateRequest?.error && (
        <Alert type="error" message={updateRequest?.error} showIcon />
      )}
    </>
  );
};

export default InitialInterviewPage;
